<template>
  <v-app class="default-layout">
    <v-navigation-drawer
      v-model="showDrawer"
      app
      :permanent="$vuetify.breakpoint.lgAndUp"
      width="220"
      class="pb-12"
    >
      <v-list>
        <v-list-item>
          <v-list-item-avatar>
            <v-img
              :src="profilePicture"
              :lazy-src="lazySrc"
              alt="Profile Avatar Image">
            </v-img>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title
              v-if="activeBasicId"
              class="primary--text">
              {{ activeBasicId }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ displayName }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider></v-divider>

      <v-list
        dense
        nav>
        <v-list-item
          v-if="hasActiveLineOfficialAccount || hasOrdersByUserId"
          link
          :to="{ name: 'Home' }"
          exact
          color="primary"
        >
          <v-list-item-icon>
            <v-icon>mdi-home</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="d-flex justify-space-between align-center">
              หน้าหลัก
              <v-avatar
                v-if="ordersByUserLength"
                color="error"
                size="24">
                <span class="white--text">{{ ordersByUserLength }}</span>
              </v-avatar>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <template v-if="hasActiveLineOfficialAccount">
          <template v-for="(item, index) in items">
            <v-list-group
              v-if="item.type === 'group'"
              :key="`group/${index}`"
              :prepend-icon="item.icon"
              no-action
              :value="item.activeRouteNames.includes($route.name)"
            >
              <v-list-item-title slot="activator">
                {{ item.title }}
              </v-list-item-title>

              <v-list-item
                v-for="(subItem, subItemIndex) in item.subItems"
                :key="`subItem/${subItemIndex}`"
                :to="subItem.to"
                @click="logMenuEvent(`existing_btn_${subItem.to.params.productType}_menu`, `click_buy_btn_${subItem.to.params.productType}_menu`)"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ subItem.title }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-group>

            <v-list-item
              v-else
              :key="index"
              link
              color="primary"
              exact
              :to="item.to">
              <v-list-item-icon>
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </template>
      </v-list>

      <v-divider v-if="hasActiveLineOfficialAccount || hasOrdersByUserId"></v-divider>

      <v-list
        dense
        nav>
        <v-list-item
          link
          :to="{ name: 'LineOfficialAccountChoose' }"
          exact
          color="primary">
          <v-list-item-icon>
            <v-icon>mdi-account-convert</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>เปลี่ยน Basic ID</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          link
          :to="{ name: 'Manual' }"
          exact
          color="primary">
          <v-list-item-icon>
            <v-icon>mdi-book-open-variant</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>คู่มือการใช้งาน</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          link
          :to="{ name: 'ContactUs' }"
          exact
          color="primary">
          <v-list-item-icon>
            <v-icon>mdi-phone-in-talk-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>ติดต่อเรา</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          link
          @click="onLogout">
          <v-list-item-icon>
            <v-icon>mdi-logout</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>ออกจากระบบ</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
      class="d-lg-none"
      app>
      <v-app-bar-nav-icon
        fixed
        @click.stop="toggleShowDrawer">
      </v-app-bar-nav-icon>
    </v-app-bar>

    <v-main>
      <slot></slot>
    </v-main>

    <v-footer class="footer-layout-defualt">
      <v-row>
        <v-col
          class="text-center"
          cols="12">
          <span
            class="link-text"
            @click="openPagePrivacyPolicy">Privacy Policy</span>
          |
          <span
            class="link-text"
            @click="openPageTermAndConditions">Terms and Conditions</span>
        </v-col>
      </v-row>
    </v-footer>
  </v-app>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import config from '@/config'

export default {
  name: 'DefaultLayout',
  props: {
    displayName: {
      type: String,
      default: ''
    },
    activeBasicId: {
      type: String,
      default: ''
    },
    profilePicture: {
      type: String,
      default: '/img/default-profile-avatar.jpg'
    },
    hasActiveLineOfficialAccount: {
      type: Boolean,
      default: false
    },
    hasOrdersByUserId: {
      type: Boolean,
      default: false
    },
    ordersByUserLength: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      showDrawer: false,
      items: [
        {
          title: 'ซื้อบริการ',
          type: 'group',
          icon: 'mdi-cart-outline',
          activeRouteNames: ['OrderCreate'],
          subItems: [
            {
              title: 'แพ็กเกจบรอดแคสต์',
              to: { name: 'OrderCreate', params: { productType: 'package' } }
            },
            {
              title: 'Message',
              to: { name: 'OrderCreate', params: { productType: 'message' } }
            },
            {
              title: 'Premium ID',
              to: { name: 'OrderCreate', params: { productType: 'premiumId' } }
            },
            {
              title: 'OA Chat',
              to: { name: 'OrderCreate', params: { productType: 'oaChat' } }
            }
          ]
        },
        { title: 'จัดการบัญชี',
          type: 'group',
          icon: 'mdi-account-circle',
          activeRouteNames: ['Profile', 'TaxAgent', 'TaxAgentCreate'],
          subItems: [
            {
              title: 'ข้อมูลลูกค้า',
              to: { name: 'Profile' }
            },
            ...(config.ENABLE_TAX_AGENT ? [{
              title: 'จัดการภาษี',
              to: { name: 'TaxAgent' }
            }] : [])
          ]
        },
        {
          title: 'รายการสั่งซื้อ',
          icon: 'mdi-file-document-edit-outline',
          to: { name: 'OrderList' }
        },
        { title: 'แจ้งการชำระเงิน', icon: 'mdi-file-plus-outline', to: { name: 'UploadSlip' } },
        { title: 'สิทธิพิเศษ', icon: 'mdi-star', to: { name: 'Privilege' } },
        { title: 'เอกสาร', icon: 'mdi-email', to: { name: 'DocumentList' } }
      ],
      lazySrc: 'https://picsum.photos/1/1'
    }
  },
  computed: {
    ...mapState('User', ['userProfile']),
    ...mapGetters({
      activeLineOfficialAccount: 'User/activeLineOfficialAccount'
    }),
    lineUid() {
      return this.userProfile?.uid || null
    }
  },
  methods: {
    toggleShowDrawer() {
      this.showDrawer = !this.showDrawer
    },
    onLogout() {
      if (this.$liff.isInClient()) {
        this.$liff.closeWindow()
      } else {
        this.$store.dispatch('QRCodePopup/setShowQR', false)
        this.$auth.logout(this.$route)
      }
    },
    openPagePrivacyPolicy() {
      const routeData = this.$router.resolve({ name: 'PrivacyPolicy' })
      window.open(routeData.href, '_blank')
    },
    openPageTermAndConditions() {
      const routeData = this.$router.resolve({ name: 'TermAndConditions' })
      window.open(routeData.href, '_blank')
    },
    logMenuEvent(label, event) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event,
        category: 'user_action',
        label,
        value: 1,
        userId: this.lineUid,
        premium_id: this.activeLineOfficialAccount.premiumId || null,
        basic_id: this.activeLineOfficialAccount.basicId || null,
        customer_status: this.activeLineOfficialAccount.isOldAccount ? 'existing' : 'new'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.default-layout {
  padding-bottom: 36px;

  .footer-layout-defualt {
    width: 100%;
    background-color: #ffffff !important;
    z-index: 9;
    position: fixed;
    bottom: 0px;
    left: 0px;
    border-top: 1px solid rgba(150, 150, 150, 0.1) !important;

    .link-text {
      cursor: pointer;
    }
  }
}

@media print {
  .default-layout {
    padding-bottom: 0px;

    .footer-layout-defualt {
      width: 0px;
      height: 0px;
      display: none;
      z-index: unset;
    }
  }
}
</style>
